import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchDashboardStats } from '../api';
import { Line, Bar } from 'react-chartjs-2'; 
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, 
    BarElement, Title, Tooltip, Legend, Filler 
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels'; 
import { FaTimesCircle, FaPaperPlane, FaFileInvoiceDollar, FaChartBar, FaMoneyBillWave, FaCheckCircle, FaInfoCircle, FaListUl, FaSearch, FaCalendarAlt, FaDownload } from 'react-icons/fa';

// 🟢 CORRECTION DES IMPORTS ICI
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler, ChartDataLabels);

const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Déc'];

const Dashboard = () => {
    const [stats, setStats] = useState({
        payees: 0, annulees: 0, emises: 0, totalProforma: 0,
        monthlyCounts: Array(12).fill(0), chiffreAffaires: 0
    });
    
    const [prestationStats, setPrestationStats] = useState([]);
    const [invoiceStateStats, setInvoiceStateStats] = useState([]);
    const [facturePrestationStats, setFacturePrestationStats] = useState([]);
    const [allFactures, setAllFactures] = useState([]); 
    
    const [loading, setLoading] = useState(true);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [filteredInvoicesCount, setFilteredInvoicesCount] = useState(0);
    const [filteredInvoicesTotal, setFilteredInvoicesTotal] = useState(0);

    const sessionData = localStorage.getItem('facsmart_user') ? JSON.parse(localStorage.getItem('facsmart_user')) : null;
    const currentUser = sessionData?.user || sessionData || {};
    const userEmail = currentUser?.email?.toLowerCase() || currentUser?.username?.toLowerCase() || '';
    const userRole = currentUser?.role?.toLowerCase() || '';
    
    // --- 🟢 IDENTIFICATION DES RÔLES ---
    const isAdmin = userRole === 'admin' || userRole === 'superadmin';
    const isDirection = userRole === 'direction' || userRole === 'dg' || userRole === 'ct'; 
    const isDaaf = userEmail === 'kh.koffi@arsn.ci' || userEmail === 'kh.koffi@gmail.com';
    const isSuperviseur = isAdmin || isDirection || isDaaf; 
    const isKbOuattara = userEmail === 'kb.ouattara@arsn.ci' || userEmail === 'kb.ouattara@gmail.com';
    const isFcGadie = userEmail === 'fc.gadie@arsn.ci' || userEmail === 'fc.gadie@gmail.com';
    const isFacturationOnly = !isSuperviseur && (isKbOuattara || userRole === 'facturation');
    const isFinanceOnly = !isSuperviseur && (isFcGadie || userRole === 'finance');

    const COLORS = {
        green: '#1A7A38', blue: '#0d6efd', red: '#dc3545', orange: '#fd7e14',
        lightRed: 'rgba(220, 53, 69, 0.6)', lightGreen: 'rgba(26, 122, 56, 0.1)'
    };

    const formatFCFA = (amount) => {
        if (amount === undefined || amount === null) return "0";
        return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    // 1. CHARGEMENT INITIAL (Stats générales)
    useEffect(() => {
        setLoading(true);
        const token = sessionData ? sessionData.token : '';
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const API_URL = isLocalhost ? 'http://localhost:5000/api' : 'https://fac.arsn.ci/api';

        Promise.all([
            fetchDashboardStats(),
            axios.get(`${API_URL}/encaissements/stats/invoice-state-by-prestation`, config).catch(() => ({ data: [] })),
            axios.get(`${API_URL}/factures?type=final`, config).catch(() => ({ data: [] }))
        ])
        .then(([statsRes, invStateRes, facturesRes]) => {
            setStats(statsRes.data || stats);
            const toutesLesFactures = facturesRes.data || [];
            setAllFactures(toutesLesFactures); 
            
            let invStats = invStateRes.data || [];
            invStats.sort((a, b) => (b.paye + b.impaiye) - (a.paye + a.impaiye));
            
            if (invStats.length > 6) {
                const top6Inv = invStats.slice(0, 6);
                const autresInv = invStats.slice(6).reduce((acc, curr) => ({
                    prestation: 'AUTRES PRESTATIONS',
                    paye: acc.paye + curr.paye,
                    impaiye: acc.impaiye + curr.impaiye
                }), { paye: 0, impaiye: 0 });
                top6Inv.push(autresInv);
                setInvoiceStateStats(top6Inv);
            } else {
                setInvoiceStateStats(invStats);
            }
            
            const agregationFactures = {};
            toutesLesFactures.forEach(facture => {
                if (facture.status !== 'annulée' && facture.lines) {
                    facture.lines.forEach(ligne => {
                        let nomPrestation = ligne.name || "Prestation générique";
                        if (nomPrestation.length > 30) nomPrestation = nomPrestation.substring(0, 30) + '...';
                        const montantLigne = (parseFloat(ligne.price) || 0) * (parseInt(ligne.quantity) || 1);
                        if (!agregationFactures[nomPrestation]) agregationFactures[nomPrestation] = 0;
                        agregationFactures[nomPrestation] += montantLigne;
                    });
                }
            });

            const sortedFacturation = Object.keys(agregationFactures)
                .map(key => ({ name: key, value: agregationFactures[key] }))
                .sort((a, b) => b.value - a.value);

            if (sortedFacturation.length > 6) {
                const top6Fac = sortedFacturation.slice(0, 6);
                const autresFacValue = sortedFacturation.slice(6).reduce((sum, item) => sum + item.value, 0);
                top6Fac.push({ name: 'AUTRES PRESTATIONS', value: autresFacValue });
                setFacturePrestationStats(top6Fac);
            } else {
                setFacturePrestationStats(sortedFacturation);
            }

            setLoading(false);
        })
        .catch((error) => {
            console.error("Erreur de chargement du dashboard:", error);
            setLoading(false);
        });
    }, []);

    // 2. FILTRAGE DYNAMIQUE (Mis à jour à chaque changement de date)
    useEffect(() => {
        const fetchPrestationsFiltrees = async () => {
            const token = sessionData ? sessionData.token : '';
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            const API_URL = isLocalhost ? 'http://localhost:5000/api' : 'https://fac.arsn.ci/api';
            
            let url = `${API_URL}/encaissements/stats/revenue-by-prestation`;
            if (startDate && endDate) {
                url += `?startDate=${startDate}&endDate=${endDate}`;
            }

            try {
                const res = await axios.get(url, config);
                let rawPrestStats = res.data || [];
                let processedPrestStats = [];
                let totalRecouvrement = 0;

                rawPrestStats.forEach(item => {
                    const nomPrestation = (item.name || '').toUpperCase();
                    if (nomPrestation.includes('SOLDE INITIAL') || nomPrestation.includes('REPORT') || nomPrestation.includes('RECOUVREMENT')) {
                        totalRecouvrement += item.value;
                    } else {
                        processedPrestStats.push(item);
                    }
                });

                if (totalRecouvrement > 0) {
                    processedPrestStats.push({
                        name: 'RECOUVREMENT (SOLDES REPORTS & DIVERS)',
                        value: totalRecouvrement
                    });
                }

                processedPrestStats.sort((a, b) => b.value - a.value);
                setPrestationStats(processedPrestStats);
            } catch (err) {
                console.error("Erreur chargement encaissements filtrés:", err);
            }
        };

        if ((startDate && endDate) || (!startDate && !endDate)) {
            fetchPrestationsFiltrees();
        }

        let validInvoices = allFactures.filter(f => f.status !== 'annulée');
        
        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            validInvoices = validInvoices.filter(f => new Date(f.date || f.createdAt) >= start);
        }
        
        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            validInvoices = validInvoices.filter(f => new Date(f.date || f.createdAt) <= end);
        }

        setFilteredInvoicesCount(validInvoices.length);
        const total = validInvoices.reduce((acc, f) => acc + (f.totalTTC || f.totalGeneral || 0), 0);
        setFilteredInvoicesTotal(total);

    }, [startDate, endDate, allFactures]);

    const totalEncaisseReel = prestationStats.reduce((sum, p) => sum + p.value, 0); 
    const totalPayeGlobal = invoiceStateStats.reduce((sum, s) => sum + s.paye, 0); 
    const totalImpayeGlobal = invoiceStateStats.reduce((sum, s) => sum + s.impaiye, 0); 
    const totalChiffreAffaires = totalPayeGlobal + totalImpayeGlobal; 
    
    const totalFacturePrestations = facturePrestationStats.reduce((sum, p) => sum + p.value, 0);
    const creancesAnterieuresRecuperees = totalEncaisseReel > totalPayeGlobal ? totalEncaisseReel - totalPayeGlobal : 0;
    const totalFacturesCourbe = stats.monthlyCounts ? stats.monthlyCounts.reduce((sum, count) => sum + count, 0) : 0;

    // --- 3. FONCTION DE GÉNÉRATION DU RAPPORT PDF ---
    const generatePDFReport = () => {
        const doc = new jsPDF();

        // Titre de section
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Rapport d'activité", 14, 20);

        // Texte d'introduction
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        
        let periodText = "cette période";
        if (startDate && endDate) {
            periodText = `la période du ${new Date(startDate).toLocaleDateString('fr-FR')} au ${new Date(endDate).toLocaleDateString('fr-FR')}`;
        } else if (startDate) {
            periodText = `la période à partir du ${new Date(startDate).toLocaleDateString('fr-FR')}`;
        }

        const caTotalStr = formatFCFA(totalFacturePrestations);
        const recettesTotalStr = formatFCFA(totalEncaisseReel);

        const introText = `Pour ${periodText}, le chiffre d'affaires est de ${caTotalStr} FCFA.`;
        const splitIntro = doc.splitTextToSize(introText, 180);
        doc.text(splitIntro, 14, 30);

        // Sous-titre du tableau
        doc.setFont("helvetica", "italic");
        doc.setFontSize(10);
        doc.text("Tableau récapitulatif du chiffre d'affaires et des recettes", 14, 45);

        // Préparation et fusion des données (Facturations et Encaissements)
        const prestationMap = {};
        
        facturePrestationStats.forEach(p => {
            if (!prestationMap[p.name]) prestationMap[p.name] = { ca: 0, recettes: 0 };
            prestationMap[p.name].ca += p.value;
        });
        
        prestationStats.forEach(p => {
            if (!prestationMap[p.name]) prestationMap[p.name] = { ca: 0, recettes: 0 };
            prestationMap[p.name].recettes += p.value;
        });

        const tableRows = [];
        Object.keys(prestationMap).sort().forEach(name => {
            tableRows.push([
                name,
                formatFCFA(prestationMap[name].ca),
                formatFCFA(prestationMap[name].recettes)
            ]);
        });

        // 🟢 CORRECTION DE L'APPEL AUTOTABLE ICI
        autoTable(doc, {
            startY: 50,
            head: [['Nature des prestations', "Chiffre d'affaires", 'Total Recettes']],
            body: tableRows,
            foot: [['TOTAL', caTotalStr, recettesTotalStr]],
            theme: 'grid',
            headStyles: { fillColor: [181, 213, 182], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
            footStyles: { fillColor: [181, 213, 182], textColor: [0, 0, 0], fontStyle: 'bold' },
            styles: { fontSize: 9, cellPadding: 4, textColor: [50, 50, 50] },
            columnStyles: {
                0: { cellWidth: 'auto', fontStyle: 'normal' },
                1: { halign: 'right' },
                2: { halign: 'right' }
            }
        });

        // Footer du document
        doc.setFontSize(9);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(100, 100, 100);
        // doc.lastAutoTable est bien peuplé par la fonction autoTable ci-dessus
        doc.text("ARSN Rapport d'activités - Généré via FacSmart", 14, doc.lastAutoTable.finalY + 15);

        // Sauvegarde
        doc.save(`Rapport_CA_Recettes_${new Date().getTime()}.pdf`);
    };

    if (loading) return (
        <div className="vh-100 vw-100 d-flex justify-content-center align-items-center bg-light">
            <div className="spinner-border text-success" role="status"></div>
        </div>
    );

    // --- CONFIGURATION DES GRAPHIQUES ---
    const lineData = {
        labels: MONTH_LABELS,
        datasets: [{
            data: stats.monthlyCounts,
            backgroundColor: (context) => {
                const ctx = context.chart.ctx;
                const gradient = ctx.createLinearGradient(0, 0, 0, 250);
                gradient.addColorStop(0, 'rgba(26, 122, 56, 0.3)');
                gradient.addColorStop(1, 'rgba(26, 122, 56, 0.0)');
                return gradient;
            },
            borderColor: COLORS.green,
            borderWidth: 3, fill: true, tension: 0.4, 
            pointRadius: 4, pointBackgroundColor: '#fff', pointBorderColor: COLORS.green, pointBorderWidth: 2,
            pointHoverRadius: 6
        }]
    };

    const verticalStackedBarData = {
        labels: invoiceStateStats.map(s => s.prestation),
        datasets: [
            { label: 'Encaissé (CA)', data: invoiceStateStats.map(s => s.paye), backgroundColor: COLORS.green, barThickness: 20, borderRadius: 4 },
            { label: 'Impayé', data: invoiceStateStats.map(s => s.impaiye), backgroundColor: COLORS.lightRed, barThickness: 20, borderRadius: 4 }
        ]
    };

    const facturationBarData = {
        labels: facturePrestationStats.map(s => s.name),
        datasets: [{
            label: 'Montant Facturé',
            data: facturePrestationStats.map(s => s.value),
            backgroundColor: COLORS.blue,
            barThickness: 30,
            borderRadius: 4
        }]
    };

    const commonOptions = { 
        responsive: true, maintainAspectRatio: false, 
        plugins: { 
            legend: { display: false }, datalabels: { display: false },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                titleFont: { size: 12, family: 'sans-serif', weight: 'bold' },
                bodyFont: { size: 13, family: 'sans-serif', weight: 'normal' }, 
                bodyColor: '#333', titleColor: '#111',
                padding: 12, cornerRadius: 6, displayColors: true, boxPadding: 4,
                borderColor: '#e0e0e0', borderWidth: 1,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }
        }, 
        layout: { padding: { right: 10, top: 10, bottom: 10 } },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 11, color: '#666' }, maxRotation: 45, minRotation: 45 } },
            y: { border: { display: false }, grid: { color: 'rgba(0,0,0,0.05)', drawBorder: false }, ticks: { font: { size: 11, color: '#666' } }, beginAtZero: true }
        }
    };

    const PrestationsTable = () => (
        <div className="d-flex flex-column h-100">
            <h6 className="fw-bold mb-3 text-dark">
                <FaListUl className="me-2 text-success" /> Détail des Encaissements <span className="text-muted fw-normal fs-6">| Par Prestation</span>
            </h6>
            
            <div className="table-responsive flex-grow-1 custom-scrollbar border rounded bg-white" style={{ maxHeight: '220px' }}>
                <table className="table table-hover align-middle mb-0 text-nowrap" style={{ fontSize: '13px' }}>
                    <thead className="bg-light sticky-top">
                        <tr>
                            <th className="text-uppercase py-2 ps-3 text-muted" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Désignation (Prestation)</th>
                            <th className="text-uppercase text-end py-2 pe-3 text-muted" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Somme Entrée</th>
                        </tr>
                    </thead>
                    <tbody>
                        {prestationStats.length > 0 ? prestationStats.map((p, i) => {
                            const isRecouvrement = p.name.includes('RECOUVREMENT');
                            return (
                                <tr key={i} className={isRecouvrement ? "bg-primary bg-opacity-10" : ""}>
                                    <td className={`fw-bold py-2 ps-3 text-wrap ${isRecouvrement ? 'text-primary' : 'text-dark'}`} style={{minWidth: '200px'}}>
                                        {p.name}
                                    </td>
                                    <td className="text-end fw-bold py-2 pe-3 text-success">{formatFCFA(p.value)} F</td>
                                </tr>
                            );
                        }) : (
                            <tr>
                                <td colSpan="2" className="text-center text-muted py-4">Aucun encaissement enregistré pour cette période</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="mt-3 p-3 rounded d-flex flex-column flex-sm-row justify-content-between align-items-sm-center text-center text-sm-start bg-light border border-danger-subtle">
                <span className="fw-bold text-uppercase mb-1 mb-sm-0 text-danger" style={{ fontSize: '12px', letterSpacing: '1px' }}>Somme Totale Encaissée</span>
                <span className="fw-bold text-danger" style={{ fontSize: '22px', letterSpacing: '-0.5px' }}>
                    {formatFCFA(totalEncaisseReel)} <span style={{ fontSize: '14px', fontWeight: 'normal' }}>FCFA</span>
                </span>
            </div>
        </div>
    );

    const FacturationHistogram = () => (
        <div className="d-flex flex-column h-100">
            <h6 className="fw-bold mb-4 text-dark">
                <FaChartBar className="me-2 text-primary" /> Volume Facturé <span className="text-muted fw-normal fs-6">| Par Prestation</span>
            </h6>
            
            <div style={{ flexGrow: 1, minHeight: '220px' }}>
                {facturePrestationStats.length > 0 ? (
                    <Bar 
                        data={facturationBarData} 
                        options={{
                            ...commonOptions,
                            plugins: {
                                ...commonOptions.plugins,
                                tooltip: { 
                                    ...commonOptions.plugins.tooltip, 
                                    callbacks: { label: (c) => ` Montant Facturé : ${formatFCFA(c.raw)} FCFA` } 
                                }
                            }
                        }} 
                    />
                ) : (
                    <div className="d-flex justify-content-center align-items-center h-100 text-muted small border rounded bg-light">
                        Aucune donnée de facturation disponible
                    </div>
                )}
            </div>

            <div className="mt-4 p-3 rounded d-flex flex-column flex-sm-row justify-content-between align-items-sm-center text-center text-sm-start bg-light border border-primary-subtle">
                <span className="fw-bold text-uppercase mb-1 mb-sm-0 text-primary" style={{ fontSize: '12px', letterSpacing: '1px' }}>Somme Totale Facturée</span>
                <span className="fw-bold text-primary" style={{ fontSize: '22px', letterSpacing: '-0.5px' }}>
                    {formatFCFA(totalFacturePrestations)} <span style={{ fontSize: '14px', fontWeight: 'normal' }}>FCFA</span>
                </span>
            </div>
        </div>
    );

    let visibleCards = [];
    let cardColClass = "col-12 col-md-6 col-lg-4"; 

    if (isFacturationOnly) {
        visibleCards = [
            { id: 'emises', label: 'Factures Émises (Total)', val: totalFacturesCourbe, icon: <FaPaperPlane/>, color: COLORS.blue },
            { id: 'proformas', label: 'Proformas', val: stats.totalProforma, icon: <FaFileInvoiceDollar/>, color: COLORS.orange },
            { id: 'annulees', label: 'Factures Annulées', val: stats.annulees, icon: <FaTimesCircle/>, color: COLORS.red }
        ];
    } else {
        visibleCards = [
            { id: 'ca', label: 'Total Facturé (Chiffre d\'Affaires)', val: totalChiffreAffaires, isMoneyMain: true, icon: <FaChartBar/>, color: COLORS.blue },
            { id: 'tresorerie', label: 'Trésorerie (Somme Encaissée Période)', val: totalEncaisseReel, isMoneyMain: true, icon: <FaMoneyBillWave/>, color: COLORS.green },
            { id: 'impayes', label: 'Somme Restante à Recouvrer', val: totalImpayeGlobal, isMoneyMain: true, icon: <FaTimesCircle/>, color: COLORS.red }
        ];
    }

    return (
        <div className="container-fluid py-4 px-3 px-md-4">
            
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3 text-center text-md-start">
                <div>
                    <h3 className="fw-bold mb-0 text-dark text-uppercase" style={{ letterSpacing: '1px' }}>
                        Tableau de Bord {isFacturationOnly ? 'Facturation' : isFinanceOnly ? 'Financier & Caisse' : 'Supervision & Direction'}
                    </h3>
                    <p className="small mb-0 mt-1 text-muted" style={{ letterSpacing: '1px' }}>VUE D'ENSEMBLE • ARSN</p>
                </div>
                <div className="bg-white border shadow-sm px-4 py-2 rounded-2 text-dark d-inline-flex align-items-center align-self-center align-self-md-auto fw-bold" style={{ fontSize: '13px' }}>
                    EXERCICE {new Date().getFullYear()} <FaChartBar className="ms-2 text-success" />
                </div>
            </div>

            {/* FILTRE D'ANALYSE PAR PÉRIODE + BOUTON RAPPORT */}
            <div className="card shadow-sm border-0 mb-4 bg-white" style={{ borderLeft: `5px solid ${COLORS.blue}` }}>
                <div className="card-body p-3 p-md-4 d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-4">
                    <div className="d-flex flex-column flex-grow-1">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="d-flex align-items-center gap-2">
                                <FaCalendarAlt className="text-primary fs-5" />
                                <span className="fw-bold text-dark text-uppercase" style={{ letterSpacing: '0.5px' }}>Analyse par Période</span>
                            </div>
                            
                            {/* BOUTON TÉLÉCHARGER LE RAPPORT */}
                            <button className="btn btn-sm btn-success d-flex align-items-center shadow-sm" onClick={generatePDFReport}>
                                <FaDownload className="me-2" /> TÉLÉCHARGER LE RAPPORT
                            </button>
                        </div>
                        
                        <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
                            <input type="date" className="form-control form-control-sm text-muted" style={{maxWidth: '200px'}} value={startDate} onChange={e => setStartDate(e.target.value)} />
                            <span className="fw-bold text-muted small px-1">au</span>
                            <input type="date" className="form-control form-control-sm text-muted" style={{maxWidth: '200px'}} value={endDate} onChange={e => setEndDate(e.target.value)} />
                            {(startDate || endDate) && (
                                <button className="btn btn-sm btn-outline-danger ms-sm-2" onClick={() => {setStartDate(''); setEndDate('');}} title="Réinitialiser">
                                    X
                                </button>
                            )}
                        </div>
                    </div>
                    
                    <div className="d-flex flex-column flex-sm-row gap-4 align-items-sm-center ps-lg-4 border-lg-start">
                        <div className="text-start text-sm-end">
                            <div className="small text-muted fw-bold text-uppercase mb-1" style={{ fontSize: '10px', letterSpacing: '1px' }}>Factures Émises Période</div>
                            <div className="fw-bold text-primary fs-4">{filteredInvoicesCount}</div>
                        </div>
                        <div className="border-start border-2 ps-sm-4 text-start text-sm-end">
                            <div className="small text-muted fw-bold text-uppercase mb-1" style={{ fontSize: '10px', letterSpacing: '1px' }}>Montant Total Période</div>
                            <div className="fw-bold text-success fs-4">{formatFCFA(filteredInvoicesTotal)} <span className="fs-6 fw-normal text-muted">F</span></div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-5">
                {visibleCards.map((item, i) => (
                    <div className={cardColClass} key={i}>
                        <div className="card shadow-sm border-0 h-100 d-flex flex-column justify-content-center p-4">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                                <h6 className="mb-0 fw-bold text-uppercase text-muted" style={{fontSize: '11px', letterSpacing: '1px'}}>{item.label}</h6>
                                <div className="p-2 rounded d-flex align-items-center justify-content-center" style={{backgroundColor: `${item.color}15`, color: item.color, width: '40px', height: '40px'}}>
                                    {item.icon}
                                </div>
                            </div>
                            <h3 className="fw-bold mb-0 text-wrap text-break text-dark" style={{fontSize: '28px', letterSpacing: '1px'}}>
                                {formatFCFA(item.val)} {item.isMoneyMain && <span className="fs-6 fw-normal ms-1 text-muted">FCFA</span>}
                            </h3>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row g-4">
                <div className="col-12 col-lg-6">
                    <div className="card shadow-sm border-0 p-4 h-100">
                        <h6 className="fw-bold mb-4 text-dark">Évolution Mensuelle <span className="text-muted fw-normal fs-6">| Volume Facturé</span></h6>
                        <div style={{ height: '320px', flexGrow: 1 }}>
                            <Line data={lineData} options={{...commonOptions, plugins: { ...commonOptions.plugins, tooltip: { ...commonOptions.plugins.tooltip, callbacks: { label: (c) => ` Factures : ${c.parsed.y}` } } } }} />
                        </div>
                    </div>
                </div>

                {(!isFacturationOnly) && (
                    <div className="col-12 col-lg-6">
                        <div className="card shadow-sm border-0 p-4 h-100">
                            <PrestationsTable />
                        </div>
                    </div>
                )}

                {isFacturationOnly && (
                    <div className="col-12 col-lg-6">
                        <div className="card shadow-sm border-0 p-4 h-100">
                            <FacturationHistogram />
                        </div>
                    </div>
                )}

                {!isFacturationOnly && (
                    <>
                        <div className="col-12 col-lg-5 mt-4">
                            <div className="card shadow-sm border-0 p-4 h-100">
                                <FacturationHistogram />
                            </div>
                        </div>

                        <div className="col-12 col-lg-7 mt-4">
                            <div className="card shadow-sm border-0 p-4 h-100 d-flex flex-column">
                                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-4">
                                    <h6 className="fw-bold mb-0 text-dark">Recouvrement CA <span className="text-muted fw-normal fs-6">| Par Prestation</span></h6>
                                    <div className="small fw-bold d-flex gap-3 text-muted">
                                        <span className="d-flex align-items-center"><span className="rounded-circle me-1" style={{backgroundColor: COLORS.green, width:'10px', height:'10px'}}></span> Encaissé (CA)</span>
                                        <span className="d-flex align-items-center"><span className="rounded-circle me-1" style={{backgroundColor: COLORS.lightRed, width:'10px', height:'10px'}}></span> Impayé</span>
                                    </div>
                                </div>
                                
                                <div style={{ flexGrow: 1, minHeight: '220px' }}>
                                    <Bar 
                                        data={verticalStackedBarData} 
                                        options={{ 
                                            ...commonOptions,
                                            plugins: { 
                                                ...commonOptions.plugins,
                                                tooltip: { ...commonOptions.plugins.tooltip, callbacks: { label: (c) => ` ${c.dataset.label} : ${formatFCFA(c.raw)} FCFA` } }
                                            },
                                            scales: { 
                                                ...commonOptions.scales,
                                                x: { stacked: true, grid: { display: false }, ticks: { font: { size: 10, color: '#666' }, maxRotation: 45, minRotation: 45 } }, 
                                                y: { stacked: true, border: { display: false }, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { font: { size: 10, color: '#666' } } } 
                                            } 
                                        }} 
                                    />
                                </div>
                                
                                {(isFinanceOnly || isSuperviseur) && (
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3 p-3 mt-4 rounded border bg-light">
                                        <div>
                                            <div className="fw-bold text-dark" style={{fontSize: '12px'}}><FaInfoCircle className="me-1 text-primary" /> Cohérence CA / Trésorerie</div>
                                            <div className="text-muted mt-1" style={{fontSize: '11px'}}>Le total en caisse est supérieur au CA car il inclut le recouvrement d'anciennes créances et divers.</div>
                                        </div>
                                        <div className="text-sm-end ps-sm-3 mt-2 mt-sm-0 pt-2 pt-sm-0 border-top border-sm-top-0 border-secondary-subtle">
                                            <div className="text-muted text-uppercase fw-bold" style={{fontSize: '9px', letterSpacing: '0.5px'}}>Créances antérieures récupérées</div>
                                            <div className="fw-bold mt-1 text-primary" style={{fontSize: '16px'}}>+ {formatFCFA(creancesAnterieuresRecuperees)} F</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Dashboard;